"use client";

import { useState } from "react";
import { useForm } from "@formeus/react";
import { ArrowRightIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "./date-picker";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type Props = {
  className?: string;
};

export function BookAVisitDialog({ className }: Props) {
  const [open, setOpen] = useState(false);

  const { values, update, submit, validations, isSubmitting } = useForm({
    initial: {
      fullName: "",
      email: "",
      phone: "",
      timeOfDay: "" as "Ujutro" | "Popodne" | "",
      selectedDate: undefined as Date | undefined,
    },
    validators: {
      fullName: ({ fullName }) =>
        fullName.length === 0
          ? new Error("Ime i prezime je obavezno")
          : undefined,
      email: ({ email }) => {
        if (email.length === 0) {
          return new Error("Email adresa je obavezna");
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          return new Error("Unesite valjanu email adresu");
        }
        return undefined;
      },

      phone: ({ phone }) => {
        if (!phone) return undefined;
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 6) {
          return new Error("Unesite valjan broj telefona");
        }
        return undefined;
      },
      timeOfDay: ({ timeOfDay }) =>
        !timeOfDay ? new Error("Vrijeme dana je obavezno") : undefined,
      selectedDate: ({ selectedDate }) =>
        !selectedDate ? new Error("Datum je obavezan") : undefined,
    },
    onSubmitForm: async (values) => {
      const formattedDate = values.selectedDate
        ? values.selectedDate.toLocaleDateString("hr-HR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "";
      const message = `Zahtjev za rezervaciju termina.\nŽeljeni datum: ${formattedDate}\nVrijeme: ${values.timeOfDay}\nMolim vas kontaktirajte me za potvrdu termina.`;

      const sendPromise = fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.fullName,
          email: values.email,
          phone: values.phone,
          message,
          date: formattedDate,
          timeOfDay: values.timeOfDay,
        }),
      }).then(async (res) => {
        if (!res.ok) {
          let errText = "Slanje nije uspjelo. Pokušajte ponovno.";
          try {
            const data = await res.json();
            if (data?.error) errText = String(data.error);
          } catch {}
          throw new Error(errText);
        }
        return res.json();
      });

      toast.promise(sendPromise, {
        loading: "Slanje zahtjeva...",
        success: "Vaša poruka je uspješno poslana. Hvala vam na poverenju!",
        error: (err) =>
          err instanceof Error
            ? err.message
            : "Došlo je do greške pri slanju. Pokušajte ponovno.",
      });

      try {
        await sendPromise;
        setOpen(false);
        update("fullName", "");
        update("email", "");
        update("phone", "");
        update("timeOfDay", "");
        update("selectedDate", undefined);
      } catch {}
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        asChild
        className={cn(
          className,
          "w-full h-16 cursor-pointer flex items-center justify-between",
        )}
      >
        <div
          className="bg-brand-700 hover:bg-brand-800 transition-all duration-100 ease-in-out w-full h-16 flex items-center justify-center px-4"
          aria-label="Zakaži posjet"
        >
          <p className="text-background text-lg tracking-[-0.04em] leading-[110%] font-medium">
            Zakažite posjet već danas
          </p>
        </div>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "p-0 gap-0 border-none shadow-none overflow-hidden",
          "h-screen w-screen max-w-none",
          "md:h-auto md:w-full md:max-w-2xl",
          "md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%]",
        )}
      >
        <div className="flex flex-col h-full">
          <DialogDescription className="hidden p-0 m-0">Desc</DialogDescription>

          <div className="flex-1 flex flex-col justify-between md:justify-start">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col h-full justify-between"
            >
              <div className="flex flex-col gap-6 p-6">
                <div className="flex flex-col gap-2">
                  <DialogTitle className="text-2xl font-medium text-left tracking-[-0.02em] leading-[110%]">
                    Zakažite posjet
                  </DialogTitle>
                  <p className="md:text-lg text-muted-foreground">
                    Ispunite obrazac ispod, i javit ćemo Vam se što je prije
                    moguće.
                  </p>
                </div>

                <div className="flex flex-col gap-4">
                  <div>
                    <Input
                      className="py-8"
                      placeholder="Ime i prezime"
                      value={values.fullName}
                      onChange={(e) => update("fullName", e.target.value)}
                    />
                    {validations.fullName.error && (
                      <p className="text-red-500 text-sm mt-1">
                        {validations.fullName.error.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Input
                      className="py-8"
                      placeholder="Email adresa"
                      type="email"
                      value={values.email}
                      onChange={(e) => update("email", e.target.value)}
                    />
                    {validations.email.error && (
                      <p className="text-red-500 text-sm mt-1">
                        {validations.email.error.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Input
                      className="py-8"
                      placeholder="Telefon"
                      type="tel"
                      value={values.phone}
                      onChange={(e) => update("phone", e.target.value)}
                    />
                    {validations.phone.error && (
                      <p className="text-red-500 text-sm mt-1">
                        {validations.phone.error.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <Select
                      value={values.timeOfDay}
                      onValueChange={(value) => {
                        update("timeOfDay", value as "Ujutro" | "Popodne");
                        if (values.selectedDate) {
                          const dayOfWeek = values.selectedDate.getDay();
                          const isValidDay =
                            (value === "Ujutro" &&
                              [4, 5, 6].includes(dayOfWeek)) ||
                            (value === "Popodne" && [2, 3].includes(dayOfWeek));
                          if (!isValidDay) {
                            update("selectedDate", undefined);
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="w-full py-8 h-auto text-base shadow-none">
                        <SelectValue placeholder="Vrijeme dana" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ujutro">Ujutro</SelectItem>
                        <SelectItem value="Popodne">Popodne</SelectItem>
                      </SelectContent>
                    </Select>
                    {validations.timeOfDay.error && (
                      <p className="text-red-500 text-sm mt-1">
                        {validations.timeOfDay.error.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <DatePicker
                      value={values.selectedDate}
                      onChange={(date) => update("selectedDate", date)}
                      timeOfDay={values.timeOfDay}
                    />
                    {validations.selectedDate.error && (
                      <p className="text-red-500 text-sm mt-1">
                        {validations.selectedDate.error.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-end mt-6 md:mt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  effect="expandIcon"
                  icon={ArrowRightIcon}
                  iconPlacement="right"
                  className="w-full text-base font-medium p-8 bg-brand-800 hover:bg-brand-800/90"
                >
                  {isSubmitting ? "Šalje se..." : "Pošaljite zahtjev"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
